import { useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { useCategories, useCreateCategory, useUpdateCategory, useDeactivateCategory } from '../hooks/useIncidents';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { SortableTable, Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';
import type { IncidentCategory } from '../types';

export function CategoriesPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<IncidentCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);

  const { data: categories = [], isLoading } = useCategories();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deactivateCategory = useDeactivateCategory();

  const handleDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await deactivateCategory.mutateAsync(categoryToDelete.id);
      toast.success('Categoria desativada com sucesso');
      setCategoryToDelete(null);
    } catch (error) {
      toast.error('Erro ao desativar categoria');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Categorias de Incidentes</h1>
          <p className="text-muted-foreground">Gerencie os tipos de ocorrências disciplinares</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Categoria
        </Button>
      </div>

      {/* Categories Table */}
      <Card>
        <CardContent className="pt-6">
          {categories.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              Nenhuma categoria cadastrada
            </div>
          ) : (
            <SortableTable data={categories} defaultSortKey="name" defaultSortDirection="asc">
              {(sortedCategories) => (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead sortable sortKey="name">
                        Nome
                      </TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead align="right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedCategories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell>
                          <div className="font-medium">{category.name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground max-w-md">
                            {category.description || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingCategory(category)}
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setCategoryToDelete({ id: category.id, name: category.name })}
                              disabled={deactivateCategory.isPending}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Desativar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SortableTable>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <CategoryModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createCategory}
        />
      )}

      {/* Edit Modal */}
      {editingCategory && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onUpdate={updateCategory}
        />
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={!!categoryToDelete}
        onOpenChange={(open) => !open && setCategoryToDelete(null)}
        title="Desativar Categoria"
        description={
          <>
            Tem certeza que deseja desativar a categoria <strong>"{categoryToDelete?.name}"</strong>?
            <br />
            <br />
            Os incidentes associados não serão afetados.
          </>
        }
        confirmText="Desativar"
        cancelText="Cancelar"
        onConfirm={handleDelete}
        variant="destructive"
        icon="delete"
        isLoading={deactivateCategory.isPending}
      />
    </div>
  );
}

// Category Modal Component
interface CategoryModalProps {
  category?: IncidentCategory;
  onClose: () => void;
  onCreate?: ReturnType<typeof useCreateCategory>;
  onUpdate?: ReturnType<typeof useUpdateCategory>;
}

function CategoryModal({ category, onClose, onCreate, onUpdate }: CategoryModalProps) {
  const [name, setName] = useState(category?.name || '');
  const [description, setDescription] = useState(category?.description || '');

  const isEditing = !!category;
  const mutation = isEditing ? onUpdate : onCreate;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      if (isEditing && onUpdate && category) {
        await onUpdate.mutateAsync({
          id: category.id,
          data: {
            name: name.trim(),
            description: description.trim() || undefined,
          },
        });
        toast.success('Categoria atualizada com sucesso');
      } else if (onCreate) {
        await onCreate.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
        });
        toast.success('Categoria criada com sucesso');
      }
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao salvar categoria';
      toast.error(message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{isEditing ? 'Editar' : 'Nova'} Categoria</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Nome <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Atraso, Falta, Comportamento inadequado"
                required
                maxLength={100}
                helperText="Máximo 100 caracteres"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Descrição (opcional)
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Descreva quando esta categoria deve ser utilizada..."
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                Máximo 500 caracteres
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" disabled={mutation?.isPending}>
                {mutation?.isPending ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Salvando...
                  </>
                ) : (
                  isEditing ? 'Atualizar' : 'Criar'
                )}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
